import {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { useSearchParams } from "react-router-dom";
import InspectionService from "../services/InspectionService";
import { AuthContext } from "./AuthContext";
import { useLocalStorage, useDebouncedCallback } from "@mantine/hooks";
import { useLocation } from "react-router-dom";

export const InspectionsContext = createContext(null);

export const InspectionsProvider = ({
  enableSearchParams = true,
  children,
}) => {
  const { dataScopeOrgId } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const currentPath = location.pathname;

  const defaultPageSize = 25;
  const paramPageIndex = parseInt(searchParams.get("pageIndex"), 10);
  const paramPageSize = parseInt(searchParams.get("pageSize"), 10);

  const getStoredPageSize = () => {
    try {
      const stored = localStorage.getItem("inspections-page-size");
      const parsed = parseInt(stored, 10);
      return Number.isNaN(parsed) ? defaultPageSize : parsed;
    } catch {
      return defaultPageSize;
    }
  };

  const initialPageIndex = Number.isNaN(paramPageIndex) ? 0 : paramPageIndex;
  const initialPageSize = !Number.isNaN(paramPageSize)
    ? paramPageSize
    : getStoredPageSize();

  const [pagination, setPagination] = useState({
    pageIndex: initialPageIndex,
    pageSize: initialPageSize,
  });

  // Persist pageSize to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem("inspections-page-size", String(pagination.pageSize));
    } catch {
      // Ignore localStorage errors
    }
  }, [pagination.pageSize]);

  const initialSearchTerm = searchParams.get("search") ?? "";
  const [search, setSearch] = useState(initialSearchTerm);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearchTerm);

  const debouncedSetSearch = useDebouncedCallback((value) => {
    setDebouncedSearch(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, 500);

  const handleSetSearch = useCallback(
    (value) => {
      setSearch(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch]
  );

  useEffect(() => {
    const params = {
      pageIndex: String(pagination.pageIndex),
      pageSize: String(pagination.pageSize),
    };
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    if (currentPath === "/inspections" && enableSearchParams) {
      setSearchParams(params, { replace: true });
    }
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    debouncedSearch,
    setSearchParams,
    currentPath,
  ]);

  const updatePagination = useCallback((updater) => {
    setPagination((prev) =>
      typeof updater === "function" ? updater(prev) : updater
    );
  }, []);

  const [inspections, setInspections] = useState([]);
  const [count, setCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);

  const [columnPinning, setColumnPinning] = useLocalStorage({
    key: "inspections-column-pinning",
    defaultValue: { left: [], right: ["mrt-row-actions"] },
    getInitialValueInEffect: false,
  });
  const [sorting, setSorting] = useLocalStorage({
    key: "inspections-sorting",
    defaultValue: [],
    getInitialValueInEffect: false,
  });
  const [columnVisibility, setColumnVisibility] = useLocalStorage({
    key: "inspections-column-visibility",
    defaultValue: {},
    getInitialValueInEffect: false,
  });
  const [columnOrder, setColumnOrder] = useLocalStorage({
    key: "inspections-column-order",
    defaultValue: [],
    getInitialValueInEffect: false,
  });

  const memoizedSorting = useMemo(() => sorting, [JSON.stringify(sorting)]);

  // Reset pageIndex synchronously when org changes (before any effects fire)
  const [prevOrgId, setPrevOrgId] = useState(dataScopeOrgId);
  if (dataScopeOrgId !== prevOrgId) {
    setPrevOrgId(dataScopeOrgId);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }

  const [fetchTrigger, setFetchTrigger] = useState(0);

  const resetPageAndRefetch = useCallback(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setFetchTrigger((prev) => prev + 1);
  }, []);

  const fetchInspections = useCallback(async () => {
    if (!dataScopeOrgId) return;
    if (inspections.length === 0) setLoading(true);
    else setIsRefetching(true);

    try {
      const {
        inspections: data,
        count: cnt,
        statusCounts: sc,
      } = await InspectionService.getInspections({
        organisation: dataScopeOrgId,
        page_size: pagination.pageSize,
        page: pagination.pageIndex + 1,
        search: debouncedSearch,
        ordering: memoizedSorting.length
          ? `${memoizedSorting[0].desc ? "-" : ""}${memoizedSorting[0].id}`
          : "",
      });

      setInspections(data);
      setCount(cnt);
      setStatusCounts(sc);
    } catch (error) {
      // Out-of-range page (a stale page for an org with fewer inspections) 404s
      // - snap back to the first page, which refetches.
      if (error?.response?.status === 404 && pagination.pageIndex > 0) {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        return;
      }
      console.error("Error fetching inspections:", error);
      setInspections([]);
      setCount(0);
      setStatusCounts({});
    } finally {
      setLoading(false);
      setIsRefetching(false);
    }
  }, [
    dataScopeOrgId,
    pagination.pageIndex,
    pagination.pageSize,
    debouncedSearch,
    memoizedSorting,
    inspections.length,
    fetchTrigger,
  ]);

  useEffect(() => {
    fetchInspections();
  }, [fetchInspections]);

  return (
    <InspectionsContext.Provider
      value={{
        inspections,
        count,
        statusCounts,
        loading,
        isRefetching,
        pagination,
        setPagination: updatePagination,
        search,
        setSearch: handleSetSearch,
        sorting,
        setSorting,
        fetchInspections,
        resetPageAndRefetch,
        columnPinning,
        setColumnPinning,
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
      }}
    >
      {children}
    </InspectionsContext.Provider>
  );
};
